import {
  type AppointmentOpenDockFormValues,
  type AppointmentFormValues,
} from 'shipments/infra/ShipmentRepository'

import { formatDate } from '../../utils/date'
import {
  type AppointmentLoadingType,
  type AppointmentSlot,
  type AppointmentMode,
  type AppointmentType,
  type AppointmentStatus,
  type StopAppointment,
} from '../domain/Appointment'

export type RawAppointment = {
  channel: string
  loading_type: AppointmentLoadingType
  misscheduling_note: string
  misscheduling_reason: string | null
  mode: AppointmentMode
  scheduled_at: string | null
  scheduled_date?: AppointmentSlot
  scheduled_time?: AppointmentSlot
  suggested_date?: AppointmentSlot
  suggested_time?: AppointmentSlot
  suggested_window_start: string | null
  suggested_window_end: string | null
  scheduled_window_start: string | null
  scheduled_window_end: string | null
  type: AppointmentType
  status: AppointmentStatus
  scheduled_by: string | null
  confirmation_number: string | null
  external_reference_uuid: string | null
  out_of_time: boolean | null
  previous_scheduled_window_start: string | null
  previous_scheduled_window_end: string | null
  acknowledge_out_of_time: boolean | null
  pending_schedule: boolean | null
} | null

function mapApiToAppointmentSlot(slot?: AppointmentSlot | null) {
  return slot ?? undefined
}

export function mapApiToAppointment(appointment?: RawAppointment): StopAppointment | undefined {
  if (!appointment) {
    return undefined
  }

  return {
    channel: appointment.channel,
    misschedulingNote: appointment.misscheduling_note || undefined,
    misschedulingReason: appointment.misscheduling_reason || undefined,
    mode: appointment.mode,
    loadingType: appointment.loading_type,
    scheduledAt: appointment.scheduled_at || undefined,
    suggestedWindowStart: appointment.suggested_window_start || undefined,
    suggestedWindowEnd: appointment.suggested_window_end || undefined,
    scheduledWindowStart: appointment.scheduled_window_start || undefined,
    scheduledWindowEnd: appointment.scheduled_window_end || undefined,
    type: appointment.type,
    status: appointment.status,
    scheduledBy: appointment.scheduled_by || undefined,
    confirmationNumber: appointment.confirmation_number || undefined,
    scheduledDate: mapApiToAppointmentSlot(appointment.scheduled_date),
    scheduledTime: mapApiToAppointmentSlot(appointment.scheduled_time),
    suggestedDate: mapApiToAppointmentSlot(appointment.suggested_date),
    suggestedTime: mapApiToAppointmentSlot(appointment.suggested_time),
    externalReferenceUuid: appointment.external_reference_uuid || undefined,
    outOfTime: appointment.out_of_time || undefined,
    previousScheduledWindowStart: appointment.previous_scheduled_window_start || undefined,
    previousScheduledWindowEnd: appointment.previous_scheduled_window_end || undefined,
    acknowledgeOutOfTime: appointment.acknowledge_out_of_time || undefined,
    pendingSchedule: appointment.pending_schedule || undefined,
  }
}

function mapAppointmentDateToApi(field: string) {
  return {
    type: 'at',
    start: formatDate(new Date(field), 'yyyy-MM-dd'),
  }
}

function mapAppointmentTimeToApi({
  time,
  timeEnd,
  timeType,
}: {
  time: string
  timeEnd?: string
  timeType: string
}) {
  if (!time) {
    return null
  }
  if (timeType === 'before') {
    return {
      type: timeType,
      end: time,
    }
  }
  if (timeType === 'between') {
    return {
      type: timeType,
      start: time,
      end: timeEnd,
    }
  }
  return {
    type: timeType,
    start: time,
  }
}

export function mapAppointmentToApi(appointment: AppointmentFormValues) {
  return {
    mode: appointment.mode,
    type: appointment.type,
    loading_type: appointment.loadingType,
    date: appointment.date,
    notify_shipper: appointment.notifyShipper,
    misscheduling_note: appointment.misschedulingNote,
    confirmation_number: appointment.confirmationNumber,
    misscheduling_reason: appointment.misschedulingReason.value,
    scheduled_date: mapAppointmentDateToApi(appointment.date),
    scheduled_time: mapAppointmentTimeToApi({
      time: `${appointment.time}`,
      timeEnd: `${appointment.timeEnd}`,
      timeType: appointment.timeType.value,
    }),
  }
}

export function mapOpenDockAppointmentToApi(appointment: AppointmentOpenDockFormValues) {
  return {
    confirmation_number: appointment.confirmationNumber,
    misscheduling_note: appointment.misschedulingNote,
    misscheduling_reason: appointment.misschedulingReason?.value || null,
  }
}
